import { MapSelectionRequest } from "../map/map-selection.interface";

/**
 * Enumeration for different geometry input methods
 */
export enum GeometryInputMethod {
  FILE = "file",
  TEXT = "text",
  MAP = "map",
  LAYER = "layer",
}

/**
 * A user layer available for geofencing selection
 */
export interface IGeofencingLayer {
  /** Unique layer identifier */
  id: string;
  /** Display name */
  name: string;
  /** WKT polygon geometry for this layer */
  wkt: string;
}

/**
 * Enumeration for geometry types after validation
 */
export enum GeometryType {
  POLYGON = "polygon",
  RADIUS = "radius",
}

/**
 * Configuration interface for geofencing dialog
 */
export interface GeofencingDialogConfig {
  /** Pre-loaded WKT geometry */
  wkt?: string;
  /** Radius in meters for point geometries */
  radiusMeters?: number;
  /** Maximum allowed radius in meters */
  maxRadius?: number;
  /** Dialog title */
  title?: string;
  /** Dialog description */
  description?: string;
  /** Initial input method */
  initialMethod?: GeometryInputMethod;
  /** Location as WKT point for radius type */
  location?: string;
  /** Whether map selection is enabled */
  canUseMapSelection?: boolean;
  /** Allowed file types for upload */
  allowedFileTypes?: string[];
  /** Maximum file size for upload */
  maxFileSize?: number;
  /** User layers available for geofencing selection (My Data) */
  availableLayers?: IGeofencingLayer[];
  /** Source of the geometry - helps determine which tab to show */
  source?: "map" | "text" | "file";
  /** Flag to indicate if WKT comes from map selection */
  fromMapSelection?: boolean;
  /**
   * Whether the embeddings search area limit (`MAX_GEOFENCING_AREA_SQUARE_KILOMETERS`) applies,
   * and whether applying warms up the embeddings cache via `SearchRequestService.validateGeometry()`.
   * Defaults to `true` — this dialog is also reused by unrelated features (e.g. the
   * custom-elements detection area picker) that need an area with no embeddings-specific
   * restriction; those callers should pass `false`.
   */
  enforceEmbeddingsAreaLimit?: boolean;
}

/**
 * Result returned by geofencing dialog
 * Enhanced format to support both WKT (for backend) and GeometryCollection (for visualization)
 */
export interface GeofencingDialogResult {
  /** Whether the operation was successful */
  success: boolean;
  /** Input method used */
  inputMethod: GeometryInputMethod;
  /** Geometry type after validation */
  type?: GeometryType;
  /** WKT geometry - always provided for backend usage */
  wkt?: string;
  /** Location as WKT point for radius type */
  location?: string;
  /** Radius in meters for radius type */
  radiusMeters?: number;
  /** To paint in map */
  displayWkt?: string;
  /** Simplified geometry type indicator */
  geometryType?: "polygon" | "circle";
  delete?: boolean;
  mapSelectionRequest?: MapSelectionRequest;
}

/**
 //  * Filter structure for geofencing operations
 //  */
export interface GeofencingFilter {
  type: GeometryType;
  wkt?: string;
  location?: string;
  radiusMeters?: number;
}

/**
 * Measured extent of a geofencing area, together with whether it exceeds the maximum
 * area the embeddings API can process.
 */
export interface IGeofencingArea {
  /** Measured area in square meters */
  squareMeters: number;
  /** Measured area in square kilometers */
  squareKilometers: number;
  /** Whether the area is above MAX_GEOFENCING_AREA_SQUARE_KILOMETERS */
  exceedsLimit: boolean;
  /** Human-readable area, e.g. "1,250 km²" */
  formatted: string;
}

/**
 * Outcome of checking whether the current geofencing configuration allows an embeddings search.
 */
export interface IGeofencingSearchValidation {
  /** Whether a search can be executed with the current geofencing configuration */
  valid: boolean;
  /** User-facing explanation when invalid, null when valid */
  reason: string | null;
  /** Measured area, or null when it cannot be measured (no geometry, or a geometry without area) */
  area: IGeofencingArea | null;
}
